// src/encounter/services/use-inserted-answer-set-mutations.ts
// The three writes of an inserted template (an answer set of its own beside the note's primary one):
//   create  → POST /api/questionnaire-responses            (Idempotency-Key, the role extension, no answers yet)
//   save    → PUT  /api/questionnaire-responses/{rowId}    (If-Match: the version this tab holds)
//   remove  → the same PUT with status `entered-in-error`  (If-Match is required)
// A PUT answers with the stored resource, and its id MAY be a NEW row (copy-on-write after a sign): the caller adopts
// `stored.id` and `stored.meta.versionId` every time. A save cancels the earlier save of the SAME answer set still in
// flight (AbortController keyed by answer set id; the cancelled one rejects with an AbortError, `isSupersededSave`).
// Every write is checked against the visit's note session (`useNoteWrite`), as the note's own save is.
import { useCallback, useMemo, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { post, put } from '@/shared/api/api-client';
import type { QuestionnaireResponseResource } from '@/types/questionnaire-response-save-api';
import { ifMatchOf, insertedRoleExtension } from '../lib/answer-set';
import { encounterAnswerSetsKey } from './use-encounter-answer-sets';
import { useNoteWrite } from './note-session';

/** What a save, or a remove, needs to know about the set it writes. */
export interface AnswerSetRef {
  answerSetId: string;
  rowId: string;
  /** The stored version (`meta.versionId`) the write is conditional on. */
  versionId: string;
}

/** Put the stored head into the cached heads: replaces the set's head (a new row id included), or appends a new set. */
export function upsertHead(
  heads: readonly QuestionnaireResponseResource[] | undefined,
  head: QuestionnaireResponseResource,
  answerSetId: string,
): QuestionnaireResponseResource[] {
  const list = heads ?? [];
  const same = (h: QuestionnaireResponseResource) => h.identifier?.value === answerSetId;
  return list.some(same) ? list.map((h) => (same(h) ? head : h)) : [...list, head];
}

/** The body carries the role extension once (a body that already has it is left as it is). */
function withRole(body: QuestionnaireResponseResource): QuestionnaireResponseResource {
  const role = insertedRoleExtension();
  const ext = body.extension ?? [];
  return ext.some((e) => e.url === role.url) ? body : { ...body, extension: [...ext, role] };
}

export function useInsertedAnswerSetMutations(encounterId: string | null) {
  const qc = useQueryClient();
  const noteWrite = useNoteWrite();
  const inFlight = useRef(new Map<string, AbortController>());
  const key = encounterAnswerSetsKey(encounterId);

  const create = useCallback(
    (body: QuestionnaireResponseResource): Promise<QuestionnaireResponseResource> =>
      noteWrite(() =>
        post<QuestionnaireResponseResource>('/api/questionnaire-responses', withRole(body), {
          headers: { 'Idempotency-Key': crypto.randomUUID() },
        }),
      ).then((stored) => {
        const setId = stored.identifier?.value;
        if (setId) qc.setQueryData<QuestionnaireResponseResource[]>(key, (heads) => upsertHead(heads, stored, setId));
        return stored;
      }),
    [noteWrite, qc, key],
  );

  const save = useCallback(
    (set: AnswerSetRef, body: QuestionnaireResponseResource, opts: { keepalive?: boolean } = {}): Promise<QuestionnaireResponseResource> =>
      noteWrite(() => {
        inFlight.current.get(set.answerSetId)?.abort();
        const abort = new AbortController();
        inFlight.current.set(set.answerSetId, abort);
        return put<QuestionnaireResponseResource>(`/api/questionnaire-responses/${encodeURIComponent(set.rowId)}`, withRole({ ...body, id: set.rowId }), {
          signal: abort.signal,
          headers: { 'If-Match': ifMatchOf(set.versionId) },
          ...(opts.keepalive ? { keepalive: true } : {}),
        }).finally(() => {
          if (inFlight.current.get(set.answerSetId) === abort) inFlight.current.delete(set.answerSetId);
        });
      }).then((stored) => {
        qc.setQueryData<QuestionnaireResponseResource[]>(key, (heads) => upsertHead(heads, stored, set.answerSetId));
        return stored;
      }),
    [noteWrite, qc, key],
  );

  const remove = useCallback(
    (set: AnswerSetRef, body: QuestionnaireResponseResource): Promise<void> =>
      noteWrite(() => {
        // Nothing of this set is left to save: a save still in flight would write the answers back.
        inFlight.current.get(set.answerSetId)?.abort();
        inFlight.current.delete(set.answerSetId);
        return put<QuestionnaireResponseResource>(
          `/api/questionnaire-responses/${encodeURIComponent(set.rowId)}`,
          withRole({ ...body, id: set.rowId, status: 'entered-in-error' }),
          { headers: { 'If-Match': ifMatchOf(set.versionId) } },
        );
      }).then(() => {
        qc.setQueryData<QuestionnaireResponseResource[]>(key, (heads) => (heads ?? []).filter((h) => h.identifier?.value !== set.answerSetId));
      }),
    [noteWrite, qc, key],
  );

  return useMemo(() => ({ create, save, remove }), [create, save, remove]);
}
